import { GraphQlPriceType } from '@hub/models/graphql';
import { DEFAULT_CURRENCY } from '@hub/libs/constants';

/**
 * This constant defines how the prices and their currency for a giving locale should be formatted.
 * This is especially used for locales where:
 * - the currency has to be shown as a smybol instead of the code
 * - the number has to be formatted in a special way (Refer to Intl.NumberFormat documentation)
 */
export const PRICE_FORMATTER_OPTIONS: Record<string, Intl.NumberFormatOptions> = {
  default: {
    style: 'currency',
    currencyDisplay: 'code',
  },
  'en-GB': {
    style: 'currency',
    currencyDisplay: 'symbol',
  },
  'pt-BR': {
    style: 'currency',
    currencyDisplay: 'symbol',
  },
  'zh-TW': {
    style: 'currency',
    currencyDisplay: 'code',
    maximumFractionDigits: 0,
  },
};

export enum PriceQuantityUnit {
  PIECE = 'piece',
  LINK = 'link',
  METER = 'm',
  MILLIMETER = 'mm',
  FEET = 'ft',
  KILOGRAM = 'kg',
  INCH = 'in',
}

export const getPriceDisplayTypeForOrderType = (orderType: string) => {
  switch (orderType) {
    case '100':
      return GraphQlPriceType.Piece;
    case '110':
      return GraphQlPriceType.PieceWithCopper;
    case '200':
      return GraphQlPriceType.LengthWithoutCopper;
    case '210':
      return GraphQlPriceType.Length;
    case '220':
      return GraphQlPriceType.LengthAndPiece;
    case '1210':
      return GraphQlPriceType.LengthWithCuttingCostWithoutCopper;
    case '1260':
      return GraphQlPriceType.LengthAndPieceWithoutCopper;
    case '1270':
      return GraphQlPriceType.LeadScrewLength;
    case '300':
      return GraphQlPriceType.PieceWithChainLengthDependency;
    case '400':
      return GraphQlPriceType.ChainLink;
    case '401':
      return GraphQlPriceType.ChainLength;
    case '1520':
      return GraphQlPriceType.TribotapeLength;
    default:
      throw new Error(`Unknown orderType ${orderType}`);
  }
};

const getPriceFormatterOptions = (locale: string, currencyCode: string): Intl.NumberFormatOptions => {
  const options: Intl.NumberFormatOptions = PRICE_FORMATTER_OPTIONS[locale] ?? PRICE_FORMATTER_OPTIONS.default;

  return {
    currency: currencyCode,
    ...options,
  };
};

/**
 * format a given price (in cent) to a string matching the locale preferences
 */
export const formatPrice = (price: number, locale: string, currencyCode: string = DEFAULT_CURRENCY): string =>
  new Intl.NumberFormat(locale, getPriceFormatterOptions(locale, currencyCode)).format(price);

/**
 * format price with a fallback
 * returns an object containing prices with and without discounts and the total discount
 */
export const displayPrice = (
  price: number | undefined,
  currencyCode: string | undefined,
  locale: string | undefined,
  isCentPrice = false,
  fallback = ''
): string => {
  if (!price || !currencyCode || !locale) {
    return fallback;
  }
  return formatPrice(isCentPrice ? (price || 0) / 100 : price, locale, currencyCode);
};
